import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import { InputSize } from "@galaxy-io/dls/inputs/Input";
import { MenuItem, MenuItemVariant } from "@galaxy-io/dls/menu/Menu";
import Text from "@galaxy-io/dls/text/Text";
import {
  type TagRule,
  type TagRuleInput,
  tagRuleFields,
  tagRuleInputSchema,
  tagRuleOperators,
} from "@gtm/contracts/core/tag-rules";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { DrawerStack } from "@/components/workspace/drawer-content";
import { FeedViewport } from "@/components/workspace/people-styles";
import { RecordTable } from "@/components/workspace/record-table";
import { SelectField } from "@/components/workspace/select-field";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { apiClient, unwrap } from "@/lib/api-client";
import { useTags } from "./tag-controls";
import { TagEditor } from "./tag-editor";

function recordLabel(count: number, company: boolean) {
  return company ? (count === 1 ? "company" : "companies") : count === 1 ? "person" : "people";
}

function ruleInput(rule: TagRule): TagRuleInput {
  const { tagId, field, operator, value, enabled } = rule;
  return { tagId, field, operator, value, enabled };
}
export function TagRulesView({
  creating,
  onCloseCreate,
}: {
  creating: boolean;
  onCloseCreate: () => void;
}) {
  const { params, update } = useUrlState();
  const q = params.get("q") || "";
  const query = useQuery({
    queryKey: ["tag-rules"],
    queryFn: ({ signal }) => unwrap(apiClient().GET("/tag-rules", { signal })),
  });
  const client = useQueryClient();
  const [editing, setEditing] = useState<TagRule | null>(null);
  const [removing, setRemoving] = useState<TagRule | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const items = (query.data ?? []).filter((r) =>
    `${r.tagName} ${tagRuleFields[r.field]} ${r.value}`.toLowerCase().includes(q.toLowerCase()),
  );
  async function change(rule: TagRule, remove = false) {
    setBusy(true);
    setError(null);
    try {
      if (remove)
        await unwrap(apiClient().DELETE("/tag-rules/{id}", { params: { path: { id: rule.id } } }));
      else
        await unwrap(
          apiClient().PUT("/tag-rules/{id}", {
            params: { path: { id: rule.id } },
            body: { ...ruleInput(rule), enabled: !rule.enabled },
          }),
        );
      setRemoving(null);
      void client.invalidateQueries();
    } catch (e) {
      setError((e as Error).message);
      if (remove) throw e;
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <BaseToolbar
        leadingActions={
          <Input
            aria-label="Search tag rules"
            placeholder="Search tag rules…"
            value={q}
            onChange={(e) => update({ q: e || null }, true)}
            type="search"
            size={InputSize.SMALL}
            label="Search tag rules"
            hideLabel
          />
        }
      />
      {error && !removing && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
      <FeedViewport>
        <RecordTable
          rowActions={(row) => row.actions}
          label="Tag rules"
          isLoading={query.isPending}
          error={query.error || undefined}
          onRetry={() => void query.refetch()}
          rowKeys={items.map((r) => r.id)}
          columns={[
            { key: "tag", label: "Tag", width: 180 },
            { key: "condition", label: "Condition", width: "auto" },
            { key: "matches", label: "Matching records", width: 160, align: "right" as const },
            { key: "status", label: "Status", width: 110 },
          ]}
          rows={items.map((r) => ({
            tag: r.tagName,
            condition: `${tagRuleFields[r.field]} · ${tagRuleOperators[r.operator].toLowerCase()} · ${r.value}`,
            matches: `${r.matches.toLocaleString()} ${recordLabel(r.matches, r.field.startsWith("company_"))}`,
            status: <Status value={r.enabled ? "active" : "paused"} />,
            actions: (
              <>
                <MenuItem label="Edit" isDisabled={busy} onSelect={() => setEditing(r)} />
                <MenuItem
                  label={r.enabled ? "Pause" : "Enable"}
                  isDisabled={busy}
                  onSelect={() => void change(r)}
                />
                <MenuItem
                  label="Delete"
                  variant={MenuItemVariant.ERROR}
                  isDisabled={busy}
                  onSelect={() => {
                    setError(null);
                    setRemoving(r);
                  }}
                />
              </>
            ),
          }))}
        />
      </FeedViewport>
      <TagRuleEditor
        open={creating || !!editing}
        rule={editing}
        onClose={() => {
          setEditing(null);
          onCloseCreate();
        }}
      />
      <ConfirmAction
        isOpen={!!removing}
        header="Delete tag rule?"
        description={
          <>
            This rule will stop assigning “{removing?.tagName}”. Manual assignments and tags
            supplied by other rules will remain.
          </>
        }
        label="Delete rule"
        isDestructive
        onOpenChange={(next) => {
          if (!next) (() => setRemoving(null))();
        }}
        onConfirm={async () => removing && change(removing, true)}
      />
    </>
  );
}
function TagRuleEditor({
  open = true,
  rule,
  onClose,
}: {
  rule: TagRule | null;
  onClose: () => void;
  open?: boolean;
}) {
  const tags = useTags();
  const [creatingTag, setCreatingTag] = useState(false);
  const client = useQueryClient();
  const [input, setInput] = useState<TagRuleInput>(
    rule
      ? ruleInput(rule)
      : { tagId: "", field: "company_domain", operator: "equals", value: "", enabled: true },
  );
  const [candidate, setCandidate] = useState<TagRuleInput | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const parsed = tagRuleInputSchema.safeParse(input);
  const preview = useQuery({
    queryKey: ["tag-rule-preview", candidate],
    enabled: !!candidate,
    queryFn: ({ signal }) =>
      unwrap(apiClient().POST("/tag-rules/preview", { body: candidate!, signal })),
  });
  useDialogReset(open, () => {
    setCreatingTag(false);
    setInput(
      rule
        ? ruleInput(rule)
        : { tagId: "", field: "company_domain", operator: "equals", value: "", enabled: true },
    );
    setCandidate(null);
    setBusy(false);
    setError(null);
  });
  function patch(value: Partial<TagRuleInput>) {
    setInput((old) => ({ ...old, ...value }));
    setCandidate(null);
    setError(null);
  }
  async function save() {
    if (!parsed.success || !candidate || !preview.data || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (rule)
        await unwrap(
          apiClient().PUT("/tag-rules/{id}", {
            params: { path: { id: rule.id } },
            body: parsed.data,
          }),
        );
      else await unwrap(apiClient().POST("/tag-rules", { body: parsed.data }));
      void client.invalidateQueries();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const company = input.field.startsWith("company_");
  const operators = input.field.endsWith("domain")
    ? (["equals", "domain"] as const)
    : input.field === "email"
      ? (["equals"] as const)
      : (["equals", "contains"] as const);
  return (
    <>
      <TagEditor
        open={creatingTag}
        tag={null}
        onClose={() => setCreatingTag(false)}
        onSaved={(tag) => patch({ tagId: tag.id })}
      />
      <SurfaceDialog
        open={open}
        flush
        title={rule ? "Edit tag rule" : "Create tag rule"}
        busy={busy}
        onClose={onClose}
        footer={
          <>
            <Button
              label="Cancel"
              variant={ButtonVariant.SECONDARY}
              isDisabled={busy}
              onClick={onClose}
            />
            <Button
              label="Preview matches"
              variant={ButtonVariant.SECONDARY}
              isDisabled={busy || !parsed.success}
              isLoading={preview.isFetching}
              onClick={() => {
                if (parsed.success) {
                  setCandidate(parsed.data);
                  if (candidate) void preview.refetch();
                }
              }}
            />
            <Button
              label={rule ? "Save rule" : "Create rule"}
              isLoading={busy}
              isDisabled={
                busy || !candidate || !preview.data || preview.isFetching || !!preview.error
              }
              onClick={() => void save()}
            />
          </>
        }
      >
        <DrawerStack>
          <Text>
            Automatically assign a tag to matching {company ? "companies" : "people"}, including
            records added later. Matches update as profiles change.
          </Text>
          <SelectField
            label="Assign tag"
            placeholder={tags.isPending ? "Loading tags…" : "Choose a tag"}
            search
            disabled={busy || !tags.data}
            value={input.tagId}
            onChange={(tagId) => patch({ tagId })}
            options={(tags.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
          />
          {tags.error && <Button label="Retry tags" onClick={() => void tags.refetch()} />}
          <Button
            label="Create a new tag"
            variant={ButtonVariant.TERTIARY}
            isDisabled={busy}
            onClick={() => setCreatingTag(true)}
          />
          <SelectField
            label="When"
            value={input.field}
            disabled={busy}
            onChange={(field) =>
              patch({ field: field as TagRuleInput["field"], operator: "equals", value: "" })
            }
            options={Object.entries(tagRuleFields).map(([value, label]) => ({ value, label }))}
          />
          <SelectField
            label="Match"
            value={input.operator}
            disabled={busy}
            onChange={(operator) => patch({ operator: operator as TagRuleInput["operator"] })}
            options={operators.map((value) => ({ value, label: tagRuleOperators[value] }))}
          />
          <Input
            aria-label="Value to match"
            placeholder={
              input.field === "company_name"
                ? "Acme"
                : input.field === "email"
                  ? "person@example.com"
                  : "example.com"
            }
            value={input.value}
            disabled={busy}
            onChange={(e) => patch({ value: e })}
            label="Value to match"
          />
          <Text>
            Matching ignores capitalization.{" "}
            {company
              ? "Company rules tag the company itself."
              : "Email rules use the person’s saved email addresses."}{" "}
            Manual tags are kept separately.
          </Text>
          {input.value && !parsed.success && input.tagId && (
            <Alert variant={AlertVariant.ERROR}>{parsed.error.issues[0]?.message}</Alert>
          )}
          {candidate && (
            <>
              {preview.isFetching && <Text>Checking matching records…</Text>}
              {preview.error && <Alert variant={AlertVariant.ERROR}>{preview.error.message}</Alert>}
              {preview.data && !preview.isFetching && (
                <>
                  <Text>
                    {preview.data.total.toLocaleString()} matching{" "}
                    {recordLabel(preview.data.total, company)}
                    {preview.data.total === 0 ? ". Future matches will receive this tag." : ""}
                  </Text>
                  {preview.data.items.map((item) => (
                    <Text key={item.id}>{item.name}</Text>
                  ))}
                  {preview.data.total > preview.data.items.length && (
                    <Text>Showing the first {preview.data.items.length} matches.</Text>
                  )}
                  {!input.enabled && <Text>This rule is paused. Enable it to assign tags.</Text>}
                </>
              )}
            </>
          )}
          {error && <Alert variant={AlertVariant.ERROR}>{error}</Alert>}
        </DrawerStack>
      </SurfaceDialog>
    </>
  );
}
